import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSite } from '../../context/SiteContext';
import { productAPI, blogAPI, techniqueAPI, testimonialAPI } from '../../services/api';
import { Button } from '../../components/common';
import './HomePage.css';

/* ── Section Hero ────────────────────────────────── */
const HeroSection = () => {
  const { get } = useSite();
  return (
  <section className="hero">
    <div className="hero-bg">
      {get('hero_image') && <img src={get('hero_image')} alt="" className="hero-bg-img" />}
      <div className="hero-overlay" />
    </div>
    <div className="container hero-content">
      <h1 className="hero-title">
        {get('hero_title', 'Personnalisez vos vêtements')}
        <br />
        <span className="hero-title-accent">Textil professionnels</span>
      </h1>
      <p className="hero-subtitle">
        {get('hero_subtitle', 'Du studio de création à la livraison — nous rendons la personnalisation textile simple, rapide et professionnelle pour votre entreprise.')}
      </p>
      <div className="hero-actions">
        <Link to="/catalogue">
          <Button variant="primary" size="xl">
            Ouvrir le studio
          </Button>
        </Link>
        <Link to="/devis-rapide">
          <Button variant="secondary" size="xl">Devis rapide gratuit</Button>
        </Link>
      </div>
      <div className="hero-stats">
        {[['100%', 'personnalisable'], ['100%', 'Satisfaction'], ['48h', 'Devis garanti'], ['+1000', 'Produits sur catalague']].map(([v, l]) => (
          <div className="hero-stat" key={l}>
            <span className="hero-stat-value">{v}</span>
            <span className="hero-stat-label">{l}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="hero-scroll-hint">
      <div className="scroll-dot" />
    </div>
  </section>
  );
};

/* ── Techniques section ──────────────────────────── */

const getTechCols = (n) => {
  if (n <= 4) return n;
  if (n % 4 === 0) return 4;
  if (n % 3 === 0) return 3;
  if (n % 2 === 0) return 4;
  return 3;
};

const TechniquesSection = () => {
  const [techniques, setTechniques] = React.useState([]);
  React.useEffect(() => {
    techniqueAPI.list().then(({data}) => setTechniques(data.data||[])).catch(()=>{});
  }, []);
  const cols = getTechCols(techniques.length || 1);
  return (
  <section className="section techniques-section">
    <div className="container">
      <div className="section-header">
        <div className="section-tag">Nos techniques</div>
        <h2 className="section-title">Nos façons d'imprimer votre marque</h2>
        <p className="section-subtitle">De la broderie aux impressions numériques — chaque technique pour chaque besoin.</p>
      </div>
      <div className="techniques-grid" style={{ '--tech-cols': cols }}>
        {techniques.map((t) => (
          <Link key={t.id} to="/techniques" className="technique-card">
            <h3 className="technique-name">{t.name}</h3>
            <p className="technique-desc">{t.description||''}</p>
            <div className="technique-qty">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 7H4a2 2 0 00-2 2v6a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/><circle cx="8" cy="13" r="1"/></svg>
              {t.min_quantity > 1 ? `Min. ${t.min_quantity} pcs` : 'Sans minimum'}
            </div>
          </Link>
        ))}
      </div>
      <div className="section-cta">
        <Link to="/techniques">
          <Button variant="secondary">Découvrir toutes les techniques</Button>
        </Link>
      </div>
    </div>
  </section>
  );
};

/* ── How it works ────────────────────────────────── */
const steps = [
  { n: '01', title: 'Choisissez votre produit', desc: 'Parcourez notre site internet ou demandez notre catalogue de vêtements. Sélectionnez le produit, la couleur et la taille qui vous convient.' },
  { n: '02', title: 'Personnalisez dans le studio', desc: 'Ajoutez vos textes et logos, positionnez-les sur le vêtement, choisissez votre technique de marquage.' },
  { n: '03', title: 'Obtenez votre devis', desc: 'Une estimation tarifaire est calculée en temps réel. Soumettez votre demande et recevez votre devis sous 48h.' },
  { n: '04', title: 'On s\'occupe du reste', desc: 'Après validation, notre équipe produit votre commande et vous livre partout en France et en Europe.' },
];

const HowItWorks = () => (
  <section className="section how-section">
    <div className="container">
      <div className="section-header">
        <div className="section-tag">Comment ça marche</div>
        <h2 className="section-title">Simple comme 4 étapes</h2>
      </div>
      <div className="steps-grid">
        {steps.map((s, i) => (
          <div key={s.n} className="step-card">
            <div className="step-number">{s.n}</div>
            <h3 className="step-title">{s.title}</h3>
            <p className="step-desc">{s.desc}</p>
            {i < steps.length - 1 && <div className="step-arrow">→</div>}
          </div>
        ))}
      </div>
      <div className="section-cta">
        <Link to="/catalogue">
          <Button variant="primary" size="lg">Commencer maintenant</Button>
        </Link>
      </div>
    </div>
  </section>
);

/* ── Featured Products ───────────────────────────── */
const FeaturedProducts = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    productAPI.list({ featured: true, published: true, per_page: 6 })
      .then(({ data }) => setProducts(data.data || []))
      .catch(() => {});
  }, []);

  if (!products.length) return null;

  return (
    <section className="section products-section">
      <div className="container">
        <div className="section-header">
          <div className="section-tag">Catalogue</div>
          <h2 className="section-title">Nos produits phares</h2>
          <p className="section-subtitle">T-shirts, polos, sweats, vestes — des produits de qualité prêts à porter votre image.</p>
        </div>
        <div className="products-grid">
          {products.map((p) => (
            <Link key={p.id} to={p.slug ? `/produit/${p.slug}` : "/catalogue"} className="product-card">
              <div className="product-img-wrap">
                {p.main_image
                  ? <img src={p.main_image} alt={p.name} className="product-img" loading="lazy" />
                  : <div className="product-img-placeholder">👕</div>
                }
                <div className="product-overlay">
                  <span className="product-studio-btn">Personnaliser →</span>
                </div>
              </div>
              <div className="product-info">
                <span className="product-cat">{p.category_name}</span>
                <h3 className="product-name">{p.name}</h3>
                <div className="product-colors">
                  {(p.available_colors || []).slice(0, 5).map((c) => (
                    <span key={c.hex} className="color-dot" style={{ background: c.hex }} title={c.name} />
                  ))}
                  {(p.available_colors || []).length > 5 && (
                    <span className="color-more">+{(p.available_colors || []).length - 5}</span>
                  )}
                </div>
                <div className="product-footer">
                  <span className="product-price">À partir de {(parseFloat(p.base_price) || 0).toFixed(2)} €</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="section-cta">
          <Link to="/catalogue"><Button variant="secondary">Voir tout le catalogue</Button></Link>
        </div>
      </div>
    </section>
  );
};

/* ── Testimonials ────────────────────────────────── */

const Testimonials = () => {
  const [testimonials, setTestimonials] = React.useState([]);

  React.useEffect(() => {
    testimonialAPI.list({ active: 1 })
      .then(({ data }) => setTestimonials(data.data || []))
      .catch(() => {});
  }, []);

  if (!testimonials.length) return null;

  // Dupliquer 3 fois pour que la boucle soit seamless
  const items = [...testimonials, ...testimonials, ...testimonials];

  return (
    <section className="section testimonials-section">
      <div className="container">
        <div className="section-header">
          <div className="section-tag">Avis clients</div>
          <h2 className="section-title">Ils nous font confiance</h2>
        </div>
      </div>

      {/* Pleine largeur pour le carousel */}
      <div className="testimonials-marquee-wrap">
        <div
          className="testimonials-marquee"
          style={{ animationDuration: `${testimonials.length * 5}s` }}
        >
          {items.map((t, i) => (
            <div key={i} className="testimonial-card">
              <div className="testimonial-stars">{'★'.repeat(Math.min(5, t.rating || 5))}</div>
              <p className="testimonial-text">"{t.text}"</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">
                  {t.avatar
                    ? <img src={t.avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                    : (t.name || '?')[0]}
                </div>
                <div>
                  <div className="testimonial-name">{t.name}</div>
                  {t.company && <div className="testimonial-company">{t.company}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── Blog Preview ────────────────────────────────── */
const BlogPreview = () => {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    blogAPI.list({ per_page: 3, status: 'published' })
      .then(({ data }) => setPosts(data.data || []))
      .catch(() => {});
  }, []);

  if (!posts.length) return null;

  return (
    <section className="section blog-section">
      <div className="container">
        <div className="section-header">
          <div className="section-tag">Blog</div>
          <h2 className="section-title">Conseils et inspirations</h2>
        </div>
        <div className="blog-grid">
          {posts.map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} className="blog-card">
              {p.featured_image && <img src={p.featured_image} alt={p.title} className="blog-img" loading="lazy" />}
              {!p.featured_image && <div className="blog-img-placeholder" />}
              <div className="blog-body">
                {p.category_name && <span className="blog-cat" style={{ color: p.category_color }}>{p.category_name}</span>}
                <h3 className="blog-title">{p.title}</h3>
                {p.excerpt && <p className="blog-excerpt">{p.excerpt}</p>}
                <div className="blog-meta">
                  <span>{p.first_name} {p.last_name}</span>
                  {p.published_at && <span>{new Date(p.published_at).toLocaleDateString('fr-FR')}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="section-cta">
          <Link to="/blog"><Button variant="secondary">Voir tous les articles</Button></Link>
        </div>
      </div>
    </section>
  );
};

/* ── CTA Banner ──────────────────────────────────── */
const CtaBanner = () => (
  <section className="cta-banner">
    <div className="container">
      <div className="cta-content">
        <h2 className="cta-title">Prêt à personnaliser ?</h2>
        <p className="cta-subtitle">Créez votre première maquette gratuitement. Devis sous 48h, sans engagement.</p>
        <div className="cta-actions">
          <Link to="/catalogue"><Button variant="primary" size="lg">Démarrer le studio</Button></Link>
          <Link to="/contact"><Button variant="ghost" size="lg" style={{color:'white',borderColor:'rgba(255,255,255,.3)'}}>Nous contacter</Button></Link>
        </div>
      </div>
    </div>
  </section>
);

/* ── HomePage ────────────────────────────────────── */
const HomePage = () => {
  const { get, loaded } = useSite();

  // Visible par défaut ('1') — masqué seulement si explicitement '0' en DB
  const showTechniques   = get('homepage_show_techniques',   '1') !== '0';
  const showTestimonials = get('homepage_show_testimonials', '1') !== '0';

  // On attend que les settings soient chargés pour éviter le flash
  if (!loaded) return (
    <div className="page-loader" style={{ minHeight: '100vh' }}>
      <div className="spinner spinner-lg" />
    </div>
  );

  return (
    <main>
      <HeroSection />
      {showTechniques   && <TechniquesSection />}
      <HowItWorks />
      <FeaturedProducts />
      {showTestimonials && <Testimonials />}
      <BlogPreview />
      <CtaBanner />
    </main>
  );
};

export default HomePage;